import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { DynamicPricingService } from '@app/features/pricing-and-availability';
import { Event } from '@app/modules/calendar/services/calendar-state.service';
import { PlanId } from '@app/shared/+state/billing';
import { UsersFacade } from '@app/shared/+state/users';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { Reservation } from '@app/shared/interfaces';
import {
  DialogData,
  DYNAMIC_PRICING_PERIOD,
  DynamicPricingOptionsPayload,
  DynamicPricingOptionsResponse,
} from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { EventsService } from '@app/shared/services/events/events.service';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { sbnbIcon, UiModule } from '@app/ui';
import { BehaviorSubject, combineLatest, delay, filter, map, Observable } from 'rxjs';
import { SimplifiedError } from '@app/shared/services/properties/properties.service';

const MANUAL_PRICING_THRESHOLD = 0.9999;

enum Step {
  NONE = 0,
  CHECK_CALENDAR_MANUAL_PRICES = 1,
  SELECT_KEEP_MANUAL_PRICES_VS_DYNAMIC_PRICING = 2,
  DYNAMIC_PRICING_INFORMATION = 3,
  DYNAMIC_PRICING_ACTIVATION = 4,
}

const ACTIVATE_DYNAMIC_PRICING_LOADING_STEPS: { description: string; icon: sbnbIcon }[] = [
  {
    description: 'Activating dynamic pricing...',
    icon: 'tick',
  },
  {
    description: 'Optimizing your prices...',
    icon: 'settings-minimal',
  },
  {
    description: 'Syncing prices to platforms, this typically takes 15 minutes...',
    icon: 'sync',
  },
];
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

@Component({
  standalone: true,
  selector: 'sbnb-dynamic-pricing-activate-modal',
  templateUrl: './dynamic-pricing-activate-modal.component.html',
  styleUrls: ['./dynamic-pricing-activate-modal.component.scss'],
  imports: [CommonModule, UiModule],
  providers: [],
})
export class DynamicPricingActivateModalComponent extends OnDestroyComponent implements OnInit, AfterViewInit {
  public step: Step = Step.CHECK_CALENDAR_MANUAL_PRICES;
  public Step = Step;
  public manuallySetPrices = 0;
  public isMobile$: Observable<boolean> = this.deviceDetectionService.$isMobile.asObservable();
  private dynamicPricingData: DynamicPricingOptionsPayload & {
    property: DialogData['property'];
    manualPricedDaysPercentage: DialogData['manualPricedDaysPercentage'];
    mode: DialogData['mode'];
  };

  @Input() public startingStep: Step;

  public activateDynamicPricingError$ = new BehaviorSubject<any>(null);
  public showSeasonalityNote$ = combineLatest([
    this.usersFacade.propertiesEntitlement$,
    this.usersFacade.currentPlanId$,
  ]).pipe(
    map(([entitlement, planId]) => {
      const planLimit = planId === PlanId.Mogul ? 3 : 2;
      return entitlement.unlimited && entitlement.current_value > planLimit;
    })
  );

  public enableOutboundDynamicPricingProgress$ = this.dynamicPricingService.enableDynamicPricingProgress$;
  public done$ = this.enableOutboundDynamicPricingProgress$.pipe(
    filter((progress) => progress === 2),
    delay(2400),
    map(() => true)
  );
  public dynamicPricingEnableSteps = ACTIVATE_DYNAMIC_PRICING_LOADING_STEPS;

  constructor(
    public dialogRef: MatDialogRef<DynamicPricingActivateModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: DialogData,
    private deviceDetectionService: DeviceDetectionService,
    private propertiesService: PropertiesService,
    private eventsService: EventsService,
    private usersFacade: UsersFacade,
    private dynamicPricingService: DynamicPricingService
  ) {
    super();
  }

  public ngOnInit() {
    const {
      property,
      dynamic_pricing_enabled,
      base_price_type,
      base_price,
      min_price,
      max_price,
      manualPricedDaysPercentage,
      mode,
    } = this.dialogData;
    this.dynamicPricingData = {
      property,
      dynamic_pricing_enabled,
      base_price_type,
      base_price,
      min_price,
      max_price,
      manualPricedDaysPercentage,
      mode,
    };
    this.manuallySetPrices = manualPricedDaysPercentage;

    if (this.startingStep) {
      this.step = this.startingStep;
    } else if ((this.dynamicPricingData.manualPricedDaysPercentage ?? 0) < MANUAL_PRICING_THRESHOLD) {
      this.step = Step.DYNAMIC_PRICING_INFORMATION;
      this.startingStep = Step.DYNAMIC_PRICING_INFORMATION;
    }
  }

  public ngAfterViewInit() {
    if (this.step === Step.CHECK_CALENDAR_MANUAL_PRICES) {
      this.checkManuallySetPrices();
    }
  }

  public openReservation(event: Reservation | Event) {
    this.dialogRef.close(event);
  }

  public moveForward() {
    this.step++;
  }

  public async stepBack() {
    if (this.step === this.startingStep) {
      this.closeModal();
      return;
    }

    if (this.step === Step.CHECK_CALENDAR_MANUAL_PRICES) {
      this.closeModal();
      return;
    } else {
      this.step--;
    }
    if (this.step === Step.CHECK_CALENDAR_MANUAL_PRICES) {
      this.checkManuallySetPrices();
    }
  }

  private async checkManuallySetPrices(forward = true) {
    await sleep(3500);

    if ((this.dynamicPricingData.manualPricedDaysPercentage ?? 0) > MANUAL_PRICING_THRESHOLD) {
      this.step = Step.SELECT_KEEP_MANUAL_PRICES_VS_DYNAMIC_PRICING;
    } else {
      this.step = forward ? Step.DYNAMIC_PRICING_INFORMATION : Step.CHECK_CALENDAR_MANUAL_PRICES;
      this.checkManuallySetPrices();
    }
  }

  public overrideManualPrices(override_manual_edits: boolean) {
    this.dynamicPricingData = { ...this.dynamicPricingData, override_manual_edits };
    this.moveForward();
  }

  public activateDynamicPricing() {
    this.moveForward();
    this.activateDynamicPricingLoading();
  }

  private activateDynamicPricingLoading() {
    this.dynamicPricingService.activateOutboundDynamicPricing();
    this.activateDynamicPricingError$.next(false);

    const { property, manualPricedDaysPercentage, mode, ...rest } = this.dynamicPricingData;
    const payload = {
      ...rest,
      outbound_dynamic_pricing_enabled: true,
    };

    this.propertiesService.setDynamicPricingOptions(property.id, payload).subscribe(
      (response) => {
        if ((response as SimplifiedError)?.error) {
          this.activateDynamicPricingError$.next(true);
        } else {
          // TODO: There's some weirdness in what the backend returns - it's not returning the updated base price
          this.dynamicPricingService.setDynamicPricingConfig({
            ...(response as DynamicPricingOptionsResponse),
            current_base_price_type: payload.base_price_type,
            outbound_dynamic_pricing_enabled: payload.outbound_dynamic_pricing_enabled,
          });
          this.dynamicPricingService.activateOutboundDynamicPricingSuccess();
        }
      },
      (error) => {
        this.activateDynamicPricingError$.next(true);
      }
    );
  }

  public closeModal() {
    this.dialogRef.close();
    if (this.step === Step.DYNAMIC_PRICING_ACTIVATION) {
      this.eventsService.fire(`dynamic-pricing-enabled-${this.dynamicPricingData.property.id}`, true);
    }
  }

  public get priceUpdateText() {
    if (this.manuallySetPrices > MANUAL_PRICING_THRESHOLD && !this.dynamicPricingData.override_manual_edits) {
      return `Your existing manual prices will stay as they are. For all other available dates, your prices will automatically update each day for the next ${DYNAMIC_PRICING_PERIOD} months based on demand, events, and other market trends.`;
    }
    return `Your prices will automatically update each day for the next ${DYNAMIC_PRICING_PERIOD} months based on demand, events, and other market trends.`;
  }
}
